import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { SalesPartnerBankAccountsModelData } from 'src/models/salesPartner/SalesPartnerBankAccountModel';
import { SalesPartnerBankAccountsServices } from 'src/services/salesPartner/SalesPartnerBankAccountsServices';
import { useCustomerData } from 'src/content/pages/customer/customerData';
import { enqueueSnackbar } from 'notistack';
import { StatusSwitch } from 'src/components/VemigoGrid/VemigoStarusFeild';
import {
  customerStyle,
  useStyles
} from 'src/theme/vemigoCustomStyles/VemigoCustomStyles';
import VemigoTextFeild from 'src/components/VemigoGrid/VemigoTextFeild';
import { useSalesPartnerData } from '../../SalesPartnerData';

interface AddProps {
  open: boolean;
  handleClose: () => void;
  onAddBankAccount: (data) => void
}

const AddSalesPartnerBankAccount: React.FC<AddProps> = ({ open, handleClose, onAddBankAccount }) => {
  const { bankAccountTypes } = useCustomerData();
  const { bankAccountsList, setBankAccountsList, salesPartner, handleGetBankAccountsList } = useSalesPartnerData()

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newData, setNewData] = useState({
    ...SalesPartnerBankAccountsModelData,
    salesPartner: salesPartner
  });

  const handleDataChange = (fieldName: string, value: any) => {
    setNewData({ ...newData, [fieldName]: value });
  };

  const handleAdd = async () => {
    setIsSubmitting(true);
    try {
      const res = await SalesPartnerBankAccountsServices.addData({
        ...newData,
        salesPartner
      });
      enqueueSnackbar(`Bank account '${newData.accountName}' added successfully`, { variant: 'success' });
      handleGetBankAccountsList(salesPartner.id, 'Get');
      setBankAccountsList([...bankAccountsList, res]);
      onAddBankAccount(res);
      handleClose();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Bank account '${newData.accountName}' not added`, { variant: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fields = [
    { id: 'accountName', title: 'Account Name', type: 'text', size: 6 },
    { id: 'accountNumber', title: 'Account Number', type: 'text', size: 6 },
    { id: 'bankName', title: 'Bank Name', type: 'text', size: 6 },
    { id: 'ifscCode', title: 'IFSC Code', type: 'text', size: 6 },
    { id: 'micrCode', title: 'MICR Code', type: 'text', size: 6 },
    {
      id: 'accountTypeId',
      title: 'Account Type',
      type: 'select',
      selectedId: newData.accountTypeId,
      size: 6,
      dataList: bankAccountTypes
    }
  ];

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle style={useStyles.dialogTitle}>Add Bank Account</DialogTitle>
      <DialogContent
        style={{ ...useStyles.bodyStyle, ...useStyles.boxSize, height: '' }}
      >
        <Grid container spacing={2}>
          {fields.map((field) => (
            <Grid item xs={field.size} key={field.id}>
              <Typography variant="h5" style={useStyles.formTitle}>
                {field.title}
              </Typography>
              {field.type === 'select' ? (
                <Autocomplete
                  options={field.dataList}
                  getOptionLabel={(option) => option.name}
                  style={useStyles.formControl}
                  fullWidth
                  value={bankAccountTypes.find(
                    (data) => data.id === field.selectedId
                  )}
                  onChange={(event, newValue) => {
                    const dataID = newValue ? newValue.id : null;
                    handleDataChange(field.id, dataID);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={field.title}
                      variant="outlined"
                    />
                  )}
                />
              ) : (
                <VemigoTextFeild
                  key={`textfield-${field.id}`}
                  style={useStyles.formControl}
                  id={field.id}
                  type={field.type}
                  fullWidth
                  readonly={false}
                  value={newData[field.id] || ''}
                  onChange={(e) => handleDataChange(field.id, e.target.value)}
                  counter={false}
                />
              )}
            </Grid>
          ))}
          <StatusSwitch
            newData={newData}
            handleSwitchChange={handleDataChange}
            field={'isActive'}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSalesPartnerBankAccount;